import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";

// Star SVG (sin dependencias de @mui/icons-material)
function StarSvg({ color = "#cfd8dc", size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill={color}
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
      />
    </svg>
  );
}

function Star({ fillPct = 0, size = 18, emptyColor, fillColor }) {
  const pct = Math.max(0, Math.min(100, fillPct));
  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "inline-block",
      }}
    >
      <StarSvg color={emptyColor} size={size} />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          width: `${pct}%`,
          overflow: "hidden",
        }}
      >
        <StarSvg color={fillColor} size={size} />
      </Box>
    </Box>
  );
}

/**
 * value: número de 0..5 con pasos 0.5
 * onChange: (newValueStars) => void
 */
export default function StarRating({
  value = 0,
  onChange,
  readOnly = false,
  size = 20,
  precision = 0.5,
  gap = 0.5,
  emptyColor = "#cfd8dc",
  fillColor = "#f7b325",
  "aria-label": ariaLabel = "rating",
}) {
  const [hover, setHover] = useState(null);

  const shown = hover != null ? hover : value;

  const stars = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= 5; i++) {
      let fillPct = 0;
      if (shown >= i) fillPct = 100;
      else if (shown >= i - 0.5) fillPct = 50;
      arr.push({ i, fillPct });
    }
    return arr;
  }, [shown]);

  const handleMove = (i, ev) => {
    if (readOnly) return;
    const rect = ev.currentTarget.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const half = x <= rect.width / 2;

    let next = half ? i - 0.5 : i;

    // si precision=1, redondea a entero
    if (precision === 1) next = Math.round(next);

    // clamp 0..5
    next = Math.max(0, Math.min(5, next));
    setHover(next);
  };

  const handleClick = (i, ev) => {
    if (readOnly) return;
    const rect = ev.currentTarget.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const half = x <= rect.width / 2;

    let next = half ? i - 0.5 : i;
    if (precision === 1) next = Math.round(next);

    next = Math.max(0, Math.min(5, next));
    onChange?.(next);
  };

  return (
    <Box
      role={readOnly ? "img" : "slider"}
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={5}
      aria-valuenow={Number(shown || 0)}
      sx={{
        display: "inline-flex",
        gap,
        alignItems: "center",
        userSelect: "none",
      }}
      onMouseLeave={() => setHover(null)}
    >
      {stars.map(({ i, fillPct }) => (
        <Box
          key={i}
          onMouseMove={(ev) => handleMove(i, ev)}
          onClick={(ev) => handleClick(i, ev)}
          sx={{
            cursor: readOnly ? "default" : "pointer",
            lineHeight: 0,
            display: "inline-flex",
          }}
        >
          <Star
            fillPct={fillPct}
            size={size}
            emptyColor={emptyColor}
            fillColor={fillColor}
          />
        </Box>
      ))}
    </Box>
  );
}
